import { Box, Typography } from '@mui/material';
import { jobsAPI } from 'api/jobsAPI';
import {
  HereCircle,
  HereMarker,
} from '../../../components/Common/HereMaps/HereNativeRouteMap';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import HereNativeRouteMap from '../../../components/Common/HereMaps/HereNativeRouteMap';

type BookingsMapProps = {
  data: any
  selectedJob: any
  setSelectedJob: (job: any) => void
  detailsOverlayHeight?: number
  detailsOverlayActive?: boolean
}

const NON_LIVE_PROGRESS = new Set([
  'Allocated',
  'Confirmed',
  'Primary_Reallocation_Pending',
  'Manual_Reallocation_Pending',
  'Manual_Intervention_Required',
  'Booking_Update_Pending',
])

const isFutureNonActiveJob = (job: any) => {
  const bookedIso = String(job?.when ?? '')
  if (!bookedIso) {
    return false
  }

  const zone = job?.timeZone || 'Europe/London'
  const bookedTime = DateTime.fromISO(bookedIso).setZone(zone)
  if (!bookedTime.isValid) {
    return false
  }

  if (job?.where?.latitude) {
    return false
  }

  if (bookedTime <= DateTime.now().setZone(zone)) {
    return false
  }

  return NON_LIVE_PROGRESS.has(String(job?.status?.progress ?? ''))
}

const isJobDelayed = (job: any) => {
  return job?.status?.status === 'Delayed'
}

const getTrackingMarkerKind = (job: any): HereMarker['kind'] => {
  const onBoard = job?.status?.progress === 'Passenger_Picked_Up'
  const inProgress = job?.status?.status === 'In_Progress'
  const waiting = job?.status?.progress === 'Arrived'
  const delayed = isJobDelayed(job)

  if (waiting) {
    return 'trackingWaiting'
  }

  if (onBoard || inProgress) {
    return 'trackingOnBoard'
  }

  return delayed ? 'trackingDelayed' : 'tracking'
}

const getCoordinatePart = (value: any, lat: boolean) => {
  if (!value || typeof value !== 'object') {
    return undefined
  }

  return lat ? value.latitude ?? value.lat : value.longitude ?? value.lng
}

const coordinatesMatch = (a: any, b: any) => {
  const aLat = getCoordinatePart(a, true)
  const aLng = getCoordinatePart(a, false)
  const bLat = getCoordinatePart(b, true)
  const bLng = getCoordinatePart(b, false)

  return (
    typeof aLat === 'number' &&
    typeof aLng === 'number' &&
    typeof bLat === 'number' &&
    typeof bLng === 'number' &&
    aLat === bLat &&
    aLng === bLng
  )
}

const BookingsMap = ({
  data,
  selectedJob,
  setSelectedJob,
  detailsOverlayHeight = 0,
  detailsOverlayActive = false,
}: BookingsMapProps) => {
  const routeAnchorRef = useRef<{
    key: string
    origin: any
  } | null>(null)

  const { data: jobData, isLoading } = useQuery({
    queryKey: ['selectedJob', selectedJob?.id],
    queryFn: async () => jobsAPI.getJobById(selectedJob?.id),
    enabled: !!selectedJob,
  })

  const activeJob = jobData?.data ?? selectedJob

  useEffect(() => {
    if (!activeJob?.id) {
      routeAnchorRef.current = null
    }
  }, [activeJob?.id])

  const tempTrackingUnavail = useMemo(() => {
    if (!activeJob?.vehicleDetails?.isTrackable) return false
    if (activeJob?.status?.progress !== 'OnRoute') return false
    return !activeJob?.where?.latitude
  }, [activeJob])

  const routeProps = useMemo(() => {
    if (!activeJob?.pickup || !activeJob?.dropOff) {
      return null
    }

    const destination = (() => {
      if (!activeJob?.vehicleDetails?.isTrackable) {
        return activeJob.dropOff
      }

      switch (activeJob?.status?.progress) {
        case 'Driver_Allocated':
        case 'OnRoute':
          return activeJob?.where?.latitude
            ? activeJob.pickup
            : activeJob.dropOff
        default:
          return activeJob.dropOff
      }
    })()

    const dynamicOrigin = (() => {
      if (!activeJob?.vehicleDetails?.isTrackable) {
        return activeJob.pickup
      }

      switch (activeJob?.status?.progress) {
        case 'Driver_Allocated':
        case 'Passenger_Picked_Up':
        case 'Driver_Location_Update':
        case 'No_Show':
        case 'OnRoute_Passenger_On_Board':
        case 'Passenger_Dropped_Off':
        case 'Arrived':
        case 'OnRoute':
          return activeJob?.where?.latitude ? activeJob.where : activeJob.pickup
        default:
          return activeJob.pickup
      }
    })()

    const hasTrackingPosition = Boolean(activeJob?.where?.latitude)
    const shouldAnchorLiveRoute =
      Boolean(activeJob?.vehicleDetails?.isTrackable) &&
      hasTrackingPosition &&
      Boolean(destination)

    const destinationLeg = coordinatesMatch(destination, activeJob?.pickup)
      ? 'pickup'
      : 'dropoff'
    const routeAnchorKey = `${activeJob?.id ?? ''}:${destinationLeg}`

    let origin = dynamicOrigin
    if (shouldAnchorLiveRoute) {
      if (routeAnchorRef.current?.key !== routeAnchorKey) {
        routeAnchorRef.current = {
          key: routeAnchorKey,
          origin: activeJob.where,
        }
      }
      origin = routeAnchorRef.current?.origin ?? dynamicOrigin
    } else {
      routeAnchorRef.current = null
    }

    const additionalStops =
      activeJob?.vehicleDetails?.isTrackable &&
      ['Driver_Allocated', 'No_Show', 'OnRoute'].includes(
        activeJob?.status?.progress,
      )
        ? []
        : activeJob?.additionalStops ?? []

    const markers: HereMarker[] = activeJob?.where?.latitude
      ? [
          {
            key: activeJob.jobReference,
            position: activeJob.where,
            kind: getTrackingMarkerKind(activeJob),
            onClick: () => setSelectedJob(activeJob),
          },
        ]
      : []

    const progress = String(activeJob?.status?.progress ?? '')
    const shouldUseCustomRouteMarkers =
      hasTrackingPosition && Boolean(destination)

    const routeTerminalMarkerKind =
      progress === 'Driver_Allocated' ||
      progress === 'Arrived' ||
      progress === 'OnRoute'
        ? 'origin'
        : null

    if (shouldUseCustomRouteMarkers && routeTerminalMarkerKind && destination) {
      markers.push({
        key: `terminal-${activeJob.jobReference}`,
        position: destination,
        kind: routeTerminalMarkerKind,
        label: routeTerminalMarkerKind === 'origin' ? 'A' : 'B',
        tooltip: destination?.formattedAddress,
        zIndexOffset: -20,
      })
    }

    if (shouldUseCustomRouteMarkers) {
      additionalStops.forEach((stop: any, index: number) => {
        markers.push({
          key: `stop-${activeJob.jobReference}-${index}`,
          position: stop,
          kind: 'stop',
          label: index + 1,
          tooltip: stop?.formattedAddress,
          zIndexOffset: -25,
        })
      })
    }

    return {
      origin,
      destination,
      additionalStops,
      markers,
      trimRouteFrom: shouldAnchorLiveRoute ? activeJob.where : undefined,
      useCustomRouteMarkers:
        shouldUseCustomRouteMarkers && Boolean(routeTerminalMarkerKind),
    }
  }, [activeJob, setSelectedJob])

  const panTarget = useMemo(() => {
    if (!activeJob) {
      return null
    }

    if (activeJob?.where?.latitude) {
      return activeJob.where
    }

    return activeJob.pickup ?? activeJob.dropOff ?? null
  }, [activeJob])

  const fleetMarkers = useMemo<HereMarker[]>(() => {
    if (activeJob || !Array.isArray(data)) {
      return []
    }

    return data
      .filter((job: any) => job?.where?.latitude)
      .map((job: any) => ({
        key: job.jobReference,
        position: job.where,
        kind: getTrackingMarkerKind(job),
        onClick: () => setSelectedJob(job),
      }))
  }, [activeJob, data, setSelectedJob])

  const pickupAndDropoffCircles = useMemo<HereCircle[]>(() => {
    if (activeJob || !Array.isArray(data)) {
      return []
    }

    return data.reduce<HereCircle[]>((acc, job: any) => {
      if (!isFutureNonActiveJob(job)) {
        return acc
      }

      if (job?.pickup) {
        acc.push({
          key: `pickup-circle-${job.jobReference}`,
          position: job.pickup,
          radius: 16,
          strokeColor: '#0b7a32',
          strokeOpacity: 0.9,
          strokeWeight: 1.5,
          fillColor: '#0b7a32',
          fillOpacity: 0.42,
          onClick: () => setSelectedJob(job),
        })
      }

      if (job?.dropOff) {
        acc.push({
          key: `dropoff-circle-${job.jobReference}`,
          position: job.dropOff,
          radius: 16,
          strokeColor: '#c62828',
          strokeOpacity: 0.9,
          strokeWeight: 1.5,
          fillColor: '#c62828',
          fillOpacity: 0.42,
          onClick: () => setSelectedJob(job),
        })
      }

      return acc
    }, [])
  }, [activeJob, data, setSelectedJob])

  const viewportPadding = useMemo(() => {
    if (!detailsOverlayActive) {
      return undefined
    }

    return {
      top: 32,
      right: 32,
      bottom: Math.max(32, detailsOverlayHeight + 24),
      left: 32,
    }
  }, [detailsOverlayActive, detailsOverlayHeight])

  return (
    <>
      {isLoading && !activeJob && (
        <Box
          component="div"
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '10px',
          }}
        >
          <Typography variant="h6" color={'#5B5B5B'}>
            Loading...
          </Typography>
        </Box>
      )}

      <HereNativeRouteMap
        origin={routeProps?.origin}
        destination={routeProps?.destination}
        additionalStops={routeProps?.additionalStops}
        trimRouteFrom={routeProps?.trimRouteFrom}
        circles={routeProps ? [] : pickupAndDropoffCircles}
        markers={routeProps ? routeProps.markers : fleetMarkers}
        showRoute={Boolean(routeProps)}
        showRouteMarkers={
          Boolean(routeProps) && !routeProps?.useCustomRouteMarkers
        }
        hideOriginMarker={Boolean(routeProps?.markers?.length)}
        allowFitBounds
        animateRoute={false}
        panTarget={routeProps ? undefined : panTarget ?? undefined}
        panZoom={routeProps ? undefined : 13}
        viewportPadding={viewportPadding}
        mapHeight={'100%'}
        mapWidth="100%"
        showMapTypeControl={true}
        mapTypeControlPosition={'top-left'}
        borderRadius={routeProps ? '8px 8px 0 0' : '8px'}
        mapOptions={{
          zoomControl: true,
          scrollWheelZoom: true,
        }}
        showTrafficOverlay={isJobDelayed(activeJob)}
      />

      {tempTrackingUnavail && (
        <Box
          component="div"
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '5px 25px',
            backgroundColor: '#5B5B5B',
          }}
        >
          <Typography variant="h6" color={'#FFFFFF'}>
            Tracking temporarily unavailable
          </Typography>
        </Box>
      )}

      {Array.isArray(data) &&
        !routeProps &&
        !fleetMarkers.length &&
        !pickupAndDropoffCircles.length &&
        !isLoading && (
          <Box
            component="div"
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '10px',
            }}
          >
            <Typography variant="h6" color={'#5B5B5B'}>
              No map data available
            </Typography>
          </Box>
        )}
    </>
  )
}

export default BookingsMap
